import { useEffect, useState } from "react";
import { useGetSuppliersQuery } from "../../Receiving/unloading/unloadingApi";
import { Col, Row } from "react-bootstrap";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import Button from "../../../components/shared/buttons/button";
import toast from "react-hot-toast";
import { useSortAndPackedBrokerageDataMutation } from "../materialEntered/materialApi";
import useTranslator from "../../../hooks/useTranslator";
import { POUND_TO_KG } from "../../../helpers/constants";
import { convertToUserTimezone, getCurrentDateTime, getCurrentDate } from "../../../helpers/Utils";

// ─── helpers ────────────────────────────────────────────────────────────────
const emptyLog = (fields) => {
  const base = { processed_date: "" };
  fields.forEach((f) => (base[f] = ""));
  return base;
};

const sumField = (logs, field) =>
  logs.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);

const SECTION_STYLES = {
  prime:    { bg: "#d4edda", border: "#c3e6cb", text: "#155724" },
  seconds:  { bg: "#cce5ff", border: "#b8daff", text: "#004085" },
  pieces:   { bg: "#f8d7da", border: "#f5c6cb", text: "#721c24" },
  rejected: { bg: "#fff3cd", border: "#ffeeba", text: "#856404" },
};

const PackingAndSortingModal = ({ show, setShow, record, mode }) => {
  const { _t } = useTranslator();
  const LB_TO_KG = POUND_TO_KG;

  const initialFormData = {
    supplier_id: "", fruit_type_id: "", fruit_name: "", fruit_type_variety: "",
    batch_code: "", entered_date_time: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
    total_entered_kg: "", total_entered_cheeks_kg: "", total_entered_strips_kg: "",
    prime_total_kg: "", prime_total_fruit_cheeks_kg: "", prime_total_fruit_strips_kg: "",
    seconds_total_kg: "", seconds_total_fruit_cheeks_kg: "", seconds_total_fruit_strips_kg: "",
    pieces_total_kg: "", rejected_strips_kg: "", rejected_cheeks_kg: "", rejected_pieces_kg: "",
    pending_kg: "", cheeks_prime_10lb_box: "", cheeks_prime_20lb_box: "",
    cheeks_seconds_20lb_box: "", strips_prime_10lb_box: "", strips_prime_20lb_box: "",
    strips_seconds_20lb_box: "", pieces_20lb_box: "", total_pallet_ready: "", status: "packaging",
  };

  const [primeCheeksLogs,   setPrimeCheeksLogs]   = useState([]);
  const [primeStripsLogs,   setPrimeStripsLogs]   = useState([]);
  const [secondsCheeksLogs, setSecondsCheeksLogs] = useState([]);
  const [secondsStripsLogs, setSecondsStripsLogs] = useState([]);
  const [piecesLogs,        setPiecesLogs]        = useState([]);
  const [rejectedLogs,      setRejectedLogs]      = useState([]);

  // capture datetime once when modal opens
  const [modalOpenTime, setModalOpenTime] = useState("");
  useEffect(() => {
    if (show) setModalOpenTime(getCurrentDateTime("YYYY-MM-DDTHH:mm"));
  }, [show]);

  // track which section has a pending (unsaved) new row — only one at a time
  const [pendingSection, setPendingSection] = useState(null);

  const [sortAndPackedData, { isLoading: isSubmitting }] = useSortAndPackedBrokerageDataMutation();
  const { data: supplierList } = useGetSuppliersQuery({ role_id: "brokerage" });
  const [formData, setFormData] = useState(initialFormData);

  // ── "Add Log" visibility: only when entered_date is a prior day ────────────
  const today = getCurrentDate("YYYY-MM-DD");
  const enteredDay = formData.entered_date_time
    ? formData.entered_date_time.slice(0, 10)   // already in user tz after load
    : today;
  // canAddLog: only when logs exist but none have today's date (empty = fields already shown)
  const hasLogForToday = primeCheeksLogs.some((l) => l.processed_at?.slice(0, 10) === today);
  const canAddLog = mode === "edit" && formData.status === "packaging" && primeCheeksLogs.length > 0 && !hasLogForToday;

  // min/max for processed_date input
  const enteredDateMin = formData.entered_date_time || "";
  const nowMax = getCurrentDateTime("YYYY-MM-DDTHH:mm");

  // ── load record ────────────────────────────────────────────────────────────
  useEffect(() => {
    if ((mode === "edit" || mode === "view") && record) {
      const upd = { ...initialFormData };
      for (const key in record) { if (key in initialFormData) upd[key] = record[key]; }
      if (!record.status || record.status === "" || record.status === "counting") upd.status = "packaging";

      if (record.packing_and_sorting) {
        const p = record.packing_and_sorting;
        upd.cheeks_prime_10lb_box   = p.cheeks_prime_10lb_box   || "";
        upd.cheeks_prime_20lb_box   = p.cheeks_prime_20lb_box   || "";
        upd.cheeks_seconds_20lb_box = p.cheeks_seconds_20lb_box || "";
        upd.strips_prime_10lb_box   = p.strips_prime_10lb_box   || "";
        upd.strips_prime_20lb_box   = p.strips_prime_20lb_box   || "";
        upd.strips_seconds_20lb_box = p.strips_seconds_20lb_box || "";
        upd.pieces_20lb_box         = p.pieces_20lb_box         || "";
        upd.total_pallet_ready      = p.total_pallet_ready      || "";
      }

      // UTC → user timezone for display
      if (upd.entered_date_time)
        upd.entered_date_time = convertToUserTimezone(upd.entered_date_time, "YYYY-MM-DDTHH:mm");

      setFormData(upd);

      // map flat daily_logs array into per-section arrays for UI
      const rawLogs = record.daily_logs || [];
      setPrimeCheeksLogs(rawLogs.map((l) => ({
        id: l.id,
        processed_at: l.processed_at ? convertToUserTimezone(l.processed_at, "YYYY-MM-DDTHH:mm") : "",
        kg: l.prime_cheeks_kg || "", boxes_10lb: l.cheeks_prime_10lb_box || "", boxes_20lb: l.cheeks_prime_20lb_box || "",
      })));
      setPrimeStripsLogs(rawLogs.map((l) => ({
        id: l.id,
        processed_at: l.processed_at ? convertToUserTimezone(l.processed_at, "YYYY-MM-DDTHH:mm") : "",
        kg: l.prime_strips_kg || "", boxes_20lb: l.strips_prime_20lb_box || "",
      })));
      setSecondsCheeksLogs(rawLogs.map((l) => ({
        id: l.id,
        processed_at: l.processed_at ? convertToUserTimezone(l.processed_at, "YYYY-MM-DDTHH:mm") : "",
        kg: l.seconds_cheeks_kg || "", boxes_20lb: l.cheeks_seconds_20lb_box || "",
      })));
      setSecondsStripsLogs(rawLogs.map((l) => ({
        id: l.id,
        processed_at: l.processed_at ? convertToUserTimezone(l.processed_at, "YYYY-MM-DDTHH:mm") : "",
        kg: l.seconds_strips_kg || "", boxes_20lb: l.strips_seconds_20lb_box || "",
      })));
      setPiecesLogs(rawLogs.map((l) => ({
        id: l.id,
        processed_at: l.processed_at ? convertToUserTimezone(l.processed_at, "YYYY-MM-DDTHH:mm") : "",
        kg: l.pieces_kg || "", boxes_20lb: l.pieces_20lb_box || "",
      })));
      setRejectedLogs(rawLogs.map((l) => ({
        id: l.id,
        processed_at: l.processed_at ? convertToUserTimezone(l.processed_at, "YYYY-MM-DDTHH:mm") : "",
        cheeks_kg: l.rejected_cheeks_kg || "", strips_kg: l.rejected_strips_kg || "", pieces_kg: l.rejected_pieces_kg || "",
      })));
      setPendingSection(null);

      // if no logs exist, auto-open one empty row for today
      if (!rawLogs.length) {
        const ts = getCurrentDateTime("YYYY-MM-DDTHH:mm");
        setPrimeCheeksLogs([{ processed_at: ts, kg: "", boxes_10lb: "", boxes_20lb: "" }]);
        setPrimeStripsLogs([{ processed_at: ts, kg: "", boxes_20lb: "" }]);
        setSecondsCheeksLogs([{ processed_at: ts, kg: "", boxes_20lb: "" }]);
        setSecondsStripsLogs([{ processed_at: ts, kg: "", boxes_20lb: "" }]);
        setPiecesLogs([{ processed_at: ts, kg: "", boxes_20lb: "" }]);
        setRejectedLogs([{ processed_at: ts, cheeks_kg: "", strips_kg: "", pieces_kg: "" }]);
      }
    }
  }, [mode, record]);

  // ── auto-sum logs → formData aggregates ───────────────────────────────────
  useEffect(() => {
    if (primeCheeksLogs.length)
      setFormData((p) => ({ ...p,
        prime_total_fruit_cheeks_kg: sumField(primeCheeksLogs, "kg").toString(),
        cheeks_prime_10lb_box: sumField(primeCheeksLogs, "boxes_10lb").toString(),
        cheeks_prime_20lb_box: sumField(primeCheeksLogs, "boxes_20lb").toString(),
      }));
  }, [primeCheeksLogs]);

  useEffect(() => {
    if (primeStripsLogs.length)
      setFormData((p) => ({ ...p,
        prime_total_fruit_strips_kg: sumField(primeStripsLogs, "kg").toString(),
        strips_prime_20lb_box: sumField(primeStripsLogs, "boxes_20lb").toString(),
      }));
  }, [primeStripsLogs]);

  useEffect(() => {
    if (secondsCheeksLogs.length)
      setFormData((p) => ({ ...p,
        seconds_total_fruit_cheeks_kg: sumField(secondsCheeksLogs, "kg").toString(),
        cheeks_seconds_20lb_box: sumField(secondsCheeksLogs, "boxes_20lb").toString(),
      }));
  }, [secondsCheeksLogs]);

  useEffect(() => {
    if (secondsStripsLogs.length)
      setFormData((p) => ({ ...p,
        seconds_total_fruit_strips_kg: sumField(secondsStripsLogs, "kg").toString(),
        strips_seconds_20lb_box: sumField(secondsStripsLogs, "boxes_20lb").toString(),
      }));
  }, [secondsStripsLogs]);

  useEffect(() => {
    if (piecesLogs.length)
      setFormData((p) => ({ ...p,
        pieces_total_kg: sumField(piecesLogs, "kg").toString(),
        pieces_20lb_box: sumField(piecesLogs, "boxes_20lb").toString(),
      }));
  }, [piecesLogs]);

  useEffect(() => {
    if (rejectedLogs.length)
      setFormData((p) => ({ ...p,
        rejected_cheeks_kg: sumField(rejectedLogs, "cheeks_kg").toString(),
        rejected_strips_kg: sumField(rejectedLogs, "strips_kg").toString(),
        rejected_pieces_kg: sumField(rejectedLogs, "pieces_kg").toString(),
      }));
  }, [rejectedLogs]);

  // ── derived totals ─────────────────────────────────────────────────────────
  useEffect(() => {
    const h = setTimeout(() => {
      if (formData?.fruit_type_variety !== "pieces") {
        const c = parseFloat(formData?.total_entered_cheeks_kg) || 0;
        const s = parseFloat(formData?.total_entered_strips_kg) || 0;
        setFormData((p) => ({ ...p, total_entered_kg: c + s }));
      }
    }, 400);
    return () => clearTimeout(h);
  }, [formData?.fruit_type_variety, formData?.total_entered_cheeks_kg, formData?.total_entered_strips_kg]);

  useEffect(() => {
    const h = setTimeout(() => {
      if (formData?.fruit_type_variety !== "pieces") {
        const c = parseFloat(formData?.prime_total_fruit_cheeks_kg) || 0;
        const s = parseFloat(formData?.prime_total_fruit_strips_kg) || 0;
        setFormData((p) => ({ ...p, prime_total_kg: c + s }));
      }
    }, 400);
    return () => clearTimeout(h);
  }, [formData?.fruit_type_variety, formData?.prime_total_fruit_cheeks_kg, formData?.prime_total_fruit_strips_kg]);

  useEffect(() => {
    const h = setTimeout(() => {
      const c = parseFloat(formData?.seconds_total_fruit_cheeks_kg) || 0;
      const s = parseFloat(formData?.seconds_total_fruit_strips_kg) || 0;
      setFormData((p) => ({ ...p, seconds_total_kg: c + s }));
    }, 400);
    return () => clearTimeout(h);
  }, [formData?.seconds_total_fruit_cheeks_kg, formData?.seconds_total_fruit_strips_kg]);

  useEffect(() => {
    const h = setTimeout(() => {
      const total   = parseFloat(formData?.total_entered_kg) || 0;
      const prime   = parseFloat(formData?.prime_total_kg) || 0;
      const seconds = parseFloat(formData?.seconds_total_kg) || 0;
      const pieces  = parseFloat(formData?.pieces_total_kg) || 0;
      const rS = parseFloat(formData?.rejected_strips_kg) || 0;
      const rC = parseFloat(formData?.rejected_cheeks_kg) || 0;
      const rP = parseFloat(formData?.rejected_pieces_kg) || 0;
      const pending = total - (prime + seconds + pieces + rS + rC + rP);
      setFormData((p) => ({ ...p, pending_kg: pending >= 0 ? parseFloat(pending.toFixed(2)) : 0 }));
    }, 400);
    return () => clearTimeout(h);
  }, [formData?.total_entered_kg, formData?.prime_total_kg, formData?.seconds_total_kg, formData?.pieces_total_kg, formData?.rejected_strips_kg, formData?.rejected_cheeks_kg, formData?.rejected_pieces_kg]);

  // ── log row helpers ────────────────────────────────────────────────────────
  // Adding a log row adds to ALL sections simultaneously (same index = same day)
  const addLogRow = (_setter, _fields, sectionKey) => {
    const ts = modalOpenTime;
    setPrimeCheeksLogs((p)   => [...p, { processed_at: ts, kg: "", boxes_10lb: "", boxes_20lb: "" }]);
    setPrimeStripsLogs((p)   => [...p, { processed_at: ts, kg: "", boxes_20lb: "" }]);
    setSecondsCheeksLogs((p) => [...p, { processed_at: ts, kg: "", boxes_20lb: "" }]);
    setSecondsStripsLogs((p) => [...p, { processed_at: ts, kg: "", boxes_20lb: "" }]);
    setPiecesLogs((p)        => [...p, { processed_at: ts, kg: "", boxes_20lb: "" }]);
    setRejectedLogs((p)      => [...p, { processed_at: ts, cheeks_kg: "", strips_kg: "", pieces_kg: "" }]);
    setPendingSection(sectionKey);
  };
  const updateLogRow = (setter, idx, field, value, sectionType) => {
    setter((p) => p.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };

      // auto-calc kg from boxes
      if (field === "boxes_10lb" || field === "boxes_20lb") {
        const b10 = parseFloat(sectionType === "prime_cheeks" ? (field === "boxes_10lb" ? value : updated.boxes_10lb) : 0) || 0;
        const b20 = parseFloat(field === "boxes_20lb" ? value : updated.boxes_20lb) || 0;
        updated.kg = ((b10 * 10) + (b20 * 20)) * LB_TO_KG;
      }

      return updated;
    }));
  };

  const handleClose = () => setShow(false);
  const handleChange = (e) => { const { name, value } = e.target; setFormData((p) => ({ ...p, [name]: value })); };

  const validateFormData = (fd) => {
    const errs = {};
    if (!fd.status || (fd.status !== "packaging" && fd.status !== "packed"))
      errs.status = _t("please_select_valid_status", "Please select a valid status.");
    if (fd.status === "packed" && fd.pending_kg > 0)
      errs.status = _t("cannot_update_status_to_packed_unitll_pending_is_not_zero", "Status cannot be 'Packed' while pending quantity remains.");

    const totalEntered = parseFloat(fd.total_entered_kg) || 0;
    const totalAdded =
      (parseFloat(fd.prime_total_kg) || 0) +
      (parseFloat(fd.seconds_total_kg) || 0) +
      (parseFloat(fd.pieces_total_kg) || 0) +
      (parseFloat(fd.rejected_cheeks_kg) || 0) +
      (parseFloat(fd.rejected_strips_kg) || 0) +
      (parseFloat(fd.rejected_pieces_kg) || 0);

    if (totalAdded > totalEntered)
      errs.total = `${_t("total_added_exceeds_entered", "Total added")} (${totalAdded.toFixed(2)} KG) ${_t("cannot_exceed_entered", "cannot exceed total entered")} (${totalEntered.toFixed(2)} KG).`;

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    let upd = { ...formData };
    if (upd.entered_date_time) upd.entered_date_time = convertToUserTimezone(upd.entered_date_time);
    const numFields = ["total_entered_kg","total_entered_cheeks_kg","total_entered_strips_kg","prime_total_kg","prime_total_fruit_cheeks_kg","prime_total_fruit_strips_kg","seconds_total_kg","seconds_total_fruit_cheeks_kg","seconds_total_fruit_strips_kg","pieces_total_kg","rejected_strips_kg","rejected_cheeks_kg","rejected_pieces_kg","pending_kg","cheeks_prime_10lb_box","cheeks_prime_20lb_box","cheeks_seconds_20lb_box","strips_prime_10lb_box","strips_prime_20lb_box","strips_seconds_20lb_box","pieces_20lb_box","total_pallet_ready"];
    numFields.forEach((k) => { upd[k] = parseFloat(upd[k]) || 0; });
    const validationErrors = validateFormData(upd);
    if (Object.keys(validationErrors).length > 0) { toast.error(Object.values(validationErrors)[0]); return; }

    // merge 6 section arrays into flat array by index — same index = same day log
    const maxLen = Math.max(primeCheeksLogs.length, primeStripsLogs.length, secondsCheeksLogs.length, secondsStripsLogs.length, piecesLogs.length, rejectedLogs.length);
    upd.daily_logs = Array.from({ length: maxLen }, (_, i) => {
      const pc = primeCheeksLogs[i]   || {};
      const ps = primeStripsLogs[i]   || {};
      const sc = secondsCheeksLogs[i] || {};
      const ss = secondsStripsLogs[i] || {};
      const pi = piecesLogs[i]        || {};
      const rj = rejectedLogs[i]      || {};
      const entry = {
        processed_at:           pc.processed_at || ps.processed_at || sc.processed_at || ss.processed_at || pi.processed_at || rj.processed_at || "",
        prime_cheeks_kg:        parseFloat(pc.kg)          || 0,
        cheeks_prime_10lb_box:  parseFloat(pc.boxes_10lb)  || 0,
        cheeks_prime_20lb_box:  parseFloat(pc.boxes_20lb)  || 0,
        prime_strips_kg:        parseFloat(ps.kg)          || 0,
        strips_prime_20lb_box:  parseFloat(ps.boxes_20lb)  || 0,
        seconds_cheeks_kg:      parseFloat(sc.kg)          || 0,
        cheeks_seconds_20lb_box:parseFloat(sc.boxes_20lb)  || 0,
        seconds_strips_kg:      parseFloat(ss.kg)          || 0,
        strips_seconds_20lb_box:parseFloat(ss.boxes_20lb)  || 0,
        pieces_kg:              parseFloat(pi.kg)          || 0,
        pieces_20lb_box:        parseFloat(pi.boxes_20lb)  || 0,
        rejected_cheeks_kg:     parseFloat(rj.cheeks_kg)   || 0,
        rejected_strips_kg:     parseFloat(rj.strips_kg)   || 0,
        rejected_pieces_kg:     parseFloat(rj.pieces_kg)   || 0,
      };
      // include id if it's an existing record
      if (pc.id || ps.id || sc.id || ss.id || pi.id || rj.id)
        entry.id = pc.id || ps.id || sc.id || ss.id || pi.id || rj.id;
      return entry;
    });
    try {
      await sortAndPackedData({ id: record.id, requestBody: upd }).unwrap();
      handleClose();
    } catch {
      toast.error(_t("failed_to_save_packing_sorting", "Failed to save packing and sorting data."));
    }
  };

  // ── reusable log table ─────────────────────────────────────────────────────
  // packedKg & totalKg passed in so we can show Packed% + Remaining inline
  const renderLogTable = ({ logs, setter, fields, labels, style, title, emoji, sectionKey }) => {
    const isView = mode !== "edit";
    const hasPending = pendingSection === sectionKey;

    return (
      <Col lg={12}>
        <div style={{ background: style.bg, padding: "15px", borderRadius: "8px", marginBottom: "15px" }}>
          {/* Section header */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 style={{ color: style.text, fontWeight: 600, marginBottom: 0 }}>{emoji} {title}</h6>
            {canAddLog && !hasPending && (
              <Button size="xs" label={`+ ${_t("add_log", "Add Log")}`} onClick={() => addLogRow(setter, fields, sectionKey)} />
            )}
            {canAddLog && hasPending && (
              <Button size="xs" label={`+ ${_t("add_log", "Add Log")}`} disabled={true} />
            )}
          </div>

          {logs.length === 0 && (
            <p style={{ color: style.text, fontSize: "13px", margin: 0, opacity: 0.7 }}>
              {_t("no_logs_recorded", "No logs recorded.")}
            </p>
          )}

          {logs.map((row, idx) => {
            const isRowToday = row.processed_at?.slice(0, 10) === today;
            const rowDisabled = isView || !isRowToday;
            return (
            <div key={idx} style={{ background: "#fff", borderRadius: "6px", padding: "12px", marginBottom: "8px", border: `1px solid ${style.border}` }}>
              <Row className="align-items-end">
                <Col lg={3}>
                  <InputWithLabel
                    label={_t("processed_date", "Processed Date")}
                    type="datetime-local"
                    name="processed_at"
                    value={row.processed_at || ""}
                    disabled={true}
                  />
                </Col>

                {fields.map((field) => (
                  <Col key={field}>
                    <InputWithLabel
                      label={labels[field] || field}
                      type="number"
                      name={field}
                      value={field === "kg" ? (parseFloat(row[field]) || 0).toFixed(2) : (row[field] || "")}
                      onChange={(e) => updateLogRow(setter, idx, field, e.target.value, sectionKey)}
                      disabled={rowDisabled || field === "kg"}
                      placeholder="0"
                    />
                  </Col>
                ))}
              </Row>
            </div>
            );
          })}

          {/* Aggregated totals */}
          {logs.length > 0 && (
            <div className="d-flex align-items-center" style={{ fontSize: "12px", color: style.text, fontWeight: 600, marginTop: "4px" }}>
              {fields.map((f) => (
                <span key={f} style={{ marginRight: "16px" }}>{labels[f]}: {sumField(logs, f).toFixed(2)}</span>
              ))}
            </div>
          )}
        </div>
      </Col>
    );
  };

  const isView   = mode !== "edit";
  const isPieces = formData.fruit_type_variety === "pieces";

  return (
    <CommonModal
      show={show} handleClose={handleClose} setShow={setShow}
      title={mode === "add" ? _t("add_record_to_packing_and_sorting", "Add Record To Packing & Sorting") : _t("update_record_to_packing_and_sorting", "Update Packing & Sorting")}
      children={formData} BatchCodeInfo={true} BatchCode={formData?.batch_code ?? ""}
      headerInfo={true}
      headername={mode === "add" ? _t("add_record_to_packing_and_sorting", "Add Record To Packing & Sorting") : _t("update_record_to_packing_and_sorting", "Update Packing & Sorting")}
    >
      <Row className="modalScroll">
        {/* Header Info */}
        <Col lg={6}>
          <SelectBox label={_t("supplier_name", "Supplier Name")} name="supplier_id" value={formData.supplier_id}
            options={supplierList?.data.map((l) => ({ value: l.id, label: l.name }))}
            onChange={handleChange} disabled={true} />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("entered_date_time", "Entered Date Time")} type="datetime-local" name="entered_date_time" value={formData.entered_date_time} onChange={handleChange} disabled={true} />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("fruit_type", "Fruit Type")} type="text" name="fruit_name" value={formData.fruit_name || record?.fruit_name || ""} disabled={true} />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("fruit_type_variety", "Fruit Type Variety")} type="text" name="fruit_type_variety"
            value={formData.fruit_type_variety ? formData.fruit_type_variety.charAt(0).toUpperCase() + formData.fruit_type_variety.slice(1) : ""} disabled={true} />
        </Col>

        {/* Total Entered */}
        <Col lg={12} className="mt-3">
          <div style={{ background: "#f8f9fa", padding: "15px", borderRadius: "8px", marginBottom: "15px" }}>
            <h6 style={{ color: "#495057", marginBottom: "15px", fontWeight: 600 }}>{_t("total_entered", "Total Entered")}</h6>
            <Row>
              {!isPieces && (
                <>
                  <Col lg={4}><InputWithLabel label={_t("cheeks_kg", "Cheeks KG")} type="number" name="total_entered_cheeks_kg" value={formData.total_entered_cheeks_kg > 0 ? formData.total_entered_cheeks_kg : ""} onChange={handleChange} disabled={true} placeholder="0" /></Col>
                  <Col lg={4}><InputWithLabel label={_t("strips_kg", "Strips KG")} type="number" name="total_entered_strips_kg" value={formData.total_entered_strips_kg > 0 ? formData.total_entered_strips_kg : ""} onChange={handleChange} disabled={true} placeholder="0" /></Col>
                </>
              )}
              <Col lg={isPieces ? 12 : 4}><InputWithLabel label={_t("total_entered_kg", "Total KG")} type="number" name="total_entered_kg" value={formData.total_entered_kg > 0 ? formData.total_entered_kg : ""} onChange={handleChange} disabled={true} placeholder="0" /></Col>
            </Row>
          </div>
        </Col>

        {!isPieces && renderLogTable({ logs: primeCheeksLogs, setter: setPrimeCheeksLogs, sectionKey: "prime_cheeks",
          fields: ["boxes_10lb", "boxes_20lb", "kg"],
          labels: { kg: _t("total_weight_kg", "Total Weight (KG)"), boxes_10lb: _t("10lb_boxes", "10lb Boxes"), boxes_20lb: _t("20lb_boxes", "20lb Boxes") },
          style: SECTION_STYLES.prime, title: _t("prime_cheeks", "Prime Cheeks"), emoji: "🥭",
        })}

        {!isPieces && renderLogTable({ logs: primeStripsLogs, setter: setPrimeStripsLogs, sectionKey: "prime_strips",
          fields: ["boxes_20lb", "kg"],
          labels: { kg: _t("total_weight_kg", "Total Weight (KG)"), boxes_20lb: _t("20lb_boxes", "20lb Boxes") },
          style: SECTION_STYLES.prime, title: _t("prime_strips", "Prime Strips"), emoji: "🥭",
        })}

        {!isPieces && renderLogTable({ logs: secondsCheeksLogs, setter: setSecondsCheeksLogs, sectionKey: "seconds_cheeks",
          fields: ["boxes_20lb", "kg"],
          labels: { kg: _t("total_weight_kg", "Total Weight (KG)"), boxes_20lb: _t("20lb_boxes", "20lb Boxes") },
          style: SECTION_STYLES.seconds, title: _t("seconds_cheeks", "Seconds Cheeks"), emoji: "🥭",
        })}

        {!isPieces && renderLogTable({ logs: secondsStripsLogs, setter: setSecondsStripsLogs, sectionKey: "seconds_strips",
          fields: ["boxes_20lb", "kg"],
          labels: { kg: _t("total_weight_kg", "Total Weight (KG)"), boxes_20lb: _t("20lb_boxes", "20lb Boxes") },
          style: SECTION_STYLES.seconds, title: _t("seconds_strips", "Seconds Strips"), emoji: "🥭",
        })}

        {renderLogTable({ logs: piecesLogs, setter: setPiecesLogs, sectionKey: "pieces",
          fields: ["boxes_20lb", "kg"],
          labels: { kg: _t("total_weight_kg", "Total Weight (KG)"), boxes_20lb: _t("20lb_boxes", "20lb Boxes") },
          style: SECTION_STYLES.pieces, title: _t("pieces", "Pieces"), emoji: "🥭",
        })}

        {renderLogTable({ logs: rejectedLogs, setter: setRejectedLogs, sectionKey: "rejected",
          fields: isPieces ? ["pieces_kg"] : ["cheeks_kg", "strips_kg", "pieces_kg"],
          labels: { cheeks_kg: _t("rejected_cheeks_kg", "Rejected Cheeks KG"), strips_kg: _t("rejected_strips_kg", "Rejected Strips KG"), pieces_kg: _t("rejected_pieces_kg", "Rejected Pieces KG") },
          style: SECTION_STYLES.rejected, title: _t("rejected", "Rejected"), emoji: "⚠️",
        })}

        {/* Pending & Status */}
        <Col lg={6}>
          <InputWithLabel label={_t("pending_kg", "Pending KG")} type="number" name="pending_kg" value={formData.pending_kg > 0 ? formData.pending_kg : ""} onChange={handleChange} disabled={true} placeholder="0" />
        </Col>
        <Col lg={6}>
          <SelectBox label={_t("status", "Status")} name="status" value={formData?.status}
            options={[
              { value: "packaging", label: _t("still_sorting_and_packing", "Still Sorting/Packing") },
              { value: "packed",    label: _t("packed", "Packed") },
            ]}
            onChange={handleChange} disabled={formData?.fruit_type_variety === "" || isView} />
        </Col>
      </Row>

      {mode === "edit" && (
        <Row className="bg-white pt-3">
          <Col className="text-end">
            <Button label={_t("save_changes", "Save Changes")} size="small" onClick={handleSubmit} disabled={isSubmitting} />
          </Col>
        </Row>
      )}
    </CommonModal>
  );
};

export default PackingAndSortingModal;
